import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { useAppStore } from '../store/useAppStore';
import { FallbackScreen } from '../components/FallbackScreen';
import { logAnalytics } from '../utils/database';
import { AlertCircle } from 'lucide-react';
import { DynamicWorkflowForm } from '../components/forms/DynamicWorkflowForm';
import { ModalContainer } from '../components/ModalContainer';
import { CheckCircle, Eye, Download } from 'lucide-react';
import { CachedImage } from '../components/CachedImage';
import { getAssetMediaType } from '../utils/formPreviewHelpers';
import { MediaPreviewModal } from '../components/MediaPreviewModal';
import { ProMaxModel } from '../types/promax';

export const PublicProMaxPage = () => {
  const { modelId: encodedModelId } = useParams<{ modelId: string }>();
  const { setSession: setGlobalSession, session: storeSession } = useAppStore();

  const [session, setSession] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authCompleted, setAuthCompleted] = useState(false);

  const [modelConfig, setModelConfig] = useState<ProMaxModel | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [generatedAssets, setGeneratedAssets] = useState<any[]>([]);
  const [previewAsset, setPreviewAsset] = useState<any>(null);

  // Decode the model_id from the URL
  const modelId = encodedModelId ? decodeURIComponent(encodedModelId) : '';

  // 1. Fetch ProMax Model Configuration
  useEffect(() => {
    const fetchModelConfig = async () => {
      if (!modelId) {
        setConfigError('Model ID is missing.');
        setConfigLoading(false);
        return;
      }

      try {
        setConfigLoading(true);
        setConfigError(null);

        console.log('📄 [PUBLIC-PROMAX-PAGE] Loading model config for:', modelId);

        const { data: modelData, error: modelError } = await supabase
          .from('promax_models')
          .select('*')
          .eq('model_id', modelId)
          .maybeSingle();

        if (modelError) {
          setConfigError(modelError.message);
          setModelConfig(null);
          return;
        }

        if (!modelData) {
          setConfigError('ProMax model not found. This model does not exist.');
          setModelConfig(null);
          return;
        }

        if (!modelData.app_id?.trim()) {
          setConfigError('This ProMax model has no App ID — set Hyper App ID in Admin → ProMax → edit model.');
          setModelConfig(null);
          return;
        }

        console.log('🤖 [PUBLIC-PROMAX-PAGE] Model config loaded:', modelData);
        setModelConfig(modelData);

      } catch (err) {
        setConfigError(err instanceof Error ? err.message : 'Failed to load model configuration.');
        setModelConfig(null);
      } finally {
        setConfigLoading(false);
      }
    };

    fetchModelConfig();
  }, [modelId]);

  // 2. Sync Supabase session from global store
  useEffect(() => {
    if (configLoading || !modelConfig) {
      return;
    }

    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setAuthError(null);

        await logAnalytics('public_promax_page_access', {
          model_id: modelId,
          timestamp: new Date().toISOString(),
          url: window.location.href
        });

        if (!storeSession) {
          await logAnalytics('public_promax_page_session_missing', {
            model_id: modelId,
            error: 'No Supabase session',
          });
          setAuthError('Please sign in to use this page.');
          setIsAuthenticated(false);
          return;
        }

        setSession(storeSession);
        setIsAuthenticated(true);
        setGlobalSession(storeSession);

        await logAnalytics('public_promax_page_session_established', {
          model_id: modelId,
          user_id: storeSession.hy_user_id,
          plan: storeSession.hy_plan_dev_name
        });
      } catch (error) {
        console.error('Public ProMax page auth error:', error);
        await logAnalytics('public_promax_page_auth_error', {
          model_id: modelId,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        setAuthError('Failed to authenticate - Please try again');
      } finally {
        setIsLoading(false);
        setAuthCompleted(true);
      }
    };

    void initializeAuth();
  }, [configLoading, modelConfig, modelId, setGlobalSession, storeSession]);


  if (configLoading || isLoading || !authCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <img
            src="https://qsiwkqsdtbfftmxbnhie.supabase.co/storage/v1/object/public/public/c599a7b3-23d3-4f3a-a44b-b1801b7cdf24.gif"
            alt="Loading..."
            className="w-[500px] h-[500px] mx-auto rounded-lg"
          />
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-elevated border border-subtle rounded-2xl p-6 text-center">
          <AlertCircle className="w-16 h-16 text-th-secondary mx-auto mb-4" />
          <h2 className="text-xl font-bold text-th-primary mb-2">Model Not Found</h2>
          <p className="text-th-secondary mb-4">{configError}</p>
        </div>
      </div>
    );
  }

  if (authError || !isAuthenticated || !session) {
    return <FallbackScreen />;
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Side - Form */}
          <div>
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-th-primary mb-2">{modelConfig?.name}</h1>
              <p className="text-th-secondary">{modelConfig?.description}</p>
            </div>

            <DynamicWorkflowForm
              model={{
                id: modelConfig?.id || '',
                model_id: modelConfig?.model_id || '',
                name: modelConfig?.name || '',
                description: modelConfig?.description || '',
                category: (modelConfig?.category === 'text' ? 'models' : modelConfig?.category) as 'models' | 'utils' | 'effects' || 'models',
                subcategory: modelConfig?.subcategory || '',
                icon_name: modelConfig?.icon_name || '',
                icon_color: modelConfig?.icon_color || '',
                icon_bg: modelConfig?.icon_bg || '',
                tags: modelConfig?.tags || [],
                fal_endpoint: modelConfig?.fal_endpoint || '',
                credit_cost: modelConfig?.credit_cost || 0,
                output_type: (modelConfig?.output_type === 'text' || modelConfig?.output_type === 'json' ? 'image' : modelConfig?.output_type) as 'image' | 'video' | 'audio' || 'image',
                estimated_time: modelConfig?.estimated_time || '',
                is_available: modelConfig?.is_active || false,
                coming_soon: modelConfig?.coming_soon || false,
                featured: modelConfig?.is_featured || false,
                input_schema: modelConfig?.input_schema || [],
                output_mapping: modelConfig?.output_mapping || {},
                fal_stream_node_id: modelConfig?.fal_stream_node_id || '',
                fal_polling_node_name: modelConfig?.fal_polling_node_name || '',
                is_long_running: modelConfig?.is_long_running || false,
                polling_interval_seconds: modelConfig?.polling_interval_seconds || 3,
              }}
              onCancel={() => window.history.back()}
              onAssetsGenerated={(assets) => setGeneratedAssets(assets)}
              appId={modelConfig?.app_id ?? ''}
            />
          </div>

          {/* Right Side - Generated Assets */}
          <div>
            <h2 className="text-xl font-bold text-th-primary mb-4">Generated Assets</h2>
            {generatedAssets.length === 0 ? (
              <div className="bg-elevated/30 border border-subtle rounded-xl p-8 text-center">
                <p className="text-th-secondary">Your generated assets will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {generatedAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="bg-elevated border border-subtle rounded-xl overflow-hidden"
                  >
                    <div className="relative aspect-video bg-elevated/50">
                      {getAssetMediaType(asset.media_url) === 'image' && (
                        <CachedImage
                          src={asset.media_url}
                          alt={asset.prompt || 'Generated asset'}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {getAssetMediaType(asset.media_url) === 'video' && (
                        <video
                          src={asset.media_url}
                          controls
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm text-status-success">
                          <CheckCircle className="w-4 h-4" />
                          <span>Complete</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPreviewAsset(asset)}
                            className="p-2 hover:bg-overlay/50 rounded-lg transition-colors"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4 text-th-secondary" />
                          </button>
                          <a
                            href={asset.media_url}
                            download
                            className="p-2 hover:bg-overlay/50 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4 text-th-secondary" />
                          </a>
                        </div>
                      </div>
                      {asset.prompt && (
                        <p className="text-xs text-th-secondary line-clamp-2">{asset.prompt}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ModalContainer />
      {previewAsset && (
        <MediaPreviewModal
          isOpen={!!previewAsset}
          asset={previewAsset}
          onClose={() => setPreviewAsset(null)}
          onDownload={() => {
            const link = document.createElement('a');
            link.href = previewAsset.media_url;
            link.download = `asset-${previewAsset.id}`;
            link.click();
          }}
        />
      )}
    </div>
  );
};
